/**
 * Opens a panel on the turn that produced something worth looking at.
 *
 * Fires once per turn: the latest completed checkpoint is the trigger, and the
 * turn it belongs to is remembered so a re-render, a reconnect, or a
 * projection replay never reopens a panel the user has since closed.
 */
import type { ScopedThreadRef } from "@afar-research/contracts";
import { scopedThreadKey } from "@afar-research/client-runtime/environment";
import { useEffect, useRef } from "react";

import { planProactivePanel } from "../proactivePanels.logic";
import { useRightPanelStore } from "../rightPanelStore";
import type { TurnDiffSummary } from "../types";
import { useClientSettings } from "./useSettings";

export function useProactivePanel(input: {
  readonly threadRef: ScopedThreadRef | null;
  readonly latestSummary: TurnDiffSummary | null;
  readonly hasLinkedPullRequest: boolean;
  readonly panelAlreadyOpen: boolean;
}): void {
  const enabled = useClientSettings((settings) => settings.proactivePanelsEnabled);
  const openPanel = useRightPanelStore((state) => state.open);
  const actedOnTurn = useRef<string | null>(null);

  const { threadRef, latestSummary, hasLinkedPullRequest, panelAlreadyOpen } = input;
  useEffect(() => {
    if (threadRef === null || latestSummary === null) return;
    if (latestSummary.status !== "ready") return;
    const turnKey = `${scopedThreadKey(threadRef)}:${latestSummary.turnId}`;
    if (actedOnTurn.current === turnKey) return;
    actedOnTurn.current = turnKey;

    const target = planProactivePanel({
      enabled,
      hasLinkedPullRequest,
      changedFileCount: latestSummary.files.length,
      changedLineCount: latestSummary.files.reduce(
        (total, file) => total + file.additions + file.deletions,
        0,
      ),
      panelAlreadyOpen,
    });
    if (target === null) return;
    openPanel(threadRef, target === "pull-requests" ? "pull-requests" : "diff");
  }, [enabled, hasLinkedPullRequest, latestSummary, openPanel, panelAlreadyOpen, threadRef]);
}
